// ABOUTME: Event log page displaying historical behavior records with sorting and editing
// ABOUTME: Provides table view for desktop and card view for mobile with accessibility support

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Fab,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Snackbar,
  Alert,
  Chip,
} from '@mui/material';
import {
  Mic as MicIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Family, BehaviorRecord, BehaviorType, Child } from '../types/api';
import { familyAPI, behaviorAPI } from '../services/api';
import dayjs, { Dayjs } from 'dayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SharedAppBar from './SharedAppBar';
import naluV1Theme from '../theme/theme.nalu-v1';
import BehaviorEventsTable, { BehaviorEventEntry } from './BehaviorEventsTable';
import { getBehaviorIcon, getBehaviorColor, getBehaviorLabel } from '../utils/behaviorUtils';
import { formatEventLocalTime, formatUtcTimeInTimezone, formatRelativeTime } from '../utils/timeUtils';

interface FamilyWithChildren extends Family {
  children: Child[];
}

interface SortConfig {
  field: keyof BehaviorEventEntry;
  direction: 'asc' | 'desc';
}

interface FilterState {
  selectedChildId: number | null;
  selectedBehaviorTypes: BehaviorType[];
}

interface ChildDropdownOption {
  value: number | null;
  label: string;
}

interface BehaviorTypeOption {
  value: BehaviorType;
  label: string;
  icon: string;
}

// Validation functions for event date editing
export const validateEventDate = (date: string, familyCreatedAt: string): boolean => {
  const eventDate = dayjs(date);
  const now = dayjs();
  const familyDate = dayjs(familyCreatedAt);
  
  return !eventDate.isAfter(now) && !eventDate.isBefore(familyDate);
};

export const getDateValidationError = (date: string, familyCreatedAt: string): string | null => {
  const eventDate = dayjs(date);
  const now = dayjs();
  const familyDate = dayjs(familyCreatedAt);
  
  if (eventDate.isAfter(now)) {
    return 'Sorry, no time machines allowed! Events can\'t be logged in the future.';
  }
  
  if (eventDate.isBefore(familyDate)) {
    return 'Event time cannot be earlier than your family was created.';
  }
  
  return null;
};

// Server-side filtering - these functions are no longer needed

export const getDefaultFilterState = (): FilterState => ({
  selectedChildId: null, // "All Children"
  selectedBehaviorTypes: [BehaviorType.Star, BehaviorType.Triangle, BehaviorType.Square, BehaviorType.FlipYourCoin, BehaviorType.MakeItRight, BehaviorType.OneOnOneTime, BehaviorType.GoalNuke, BehaviorType.Reset]
});

export const isValidFilterState = (state: unknown): boolean => {
  if (!state || typeof state !== 'object') return false;
  const candidate = state as Record<string, unknown>;
  if (candidate.selectedChildId !== null && typeof candidate.selectedChildId !== 'number') return false;
  if (!Array.isArray(candidate.selectedBehaviorTypes)) return false;
  return true;
};

export const getEventCounterText = (filtered: number, total: number): string => {
  return `Showing ${filtered} of ${total} events`;
};

export const getChildDropdownOptions = (children: Child[]): ChildDropdownOption[] => {
  const options: ChildDropdownOption[] = [{ value: null, label: 'All Children' }];
  children.forEach(child => {
    const childId = child.childId || child.id;
    if (childId) {
      options.push({ value: childId, label: child.name });
    }
  });
  return options;
};

export const getBehaviorTypeOptions = (): BehaviorTypeOption[] => [
  { value: BehaviorType.Star, label: 'Positive', icon: 'star' },
  { value: BehaviorType.Triangle, label: 'Warning', icon: 'triangle' },
  { value: BehaviorType.Square, label: 'Consequence', icon: 'square' },
  { value: BehaviorType.FlipYourCoin, label: 'Flip Your Coin', icon: 'coin' },
  { value: BehaviorType.MakeItRight, label: 'Make It Right', icon: 'handshake' },
  { value: BehaviorType.OneOnOneTime, label: 'Let Go', icon: 'clock' },
  { value: BehaviorType.GoalNuke, label: 'Nuclear Reset', icon: 'nuke' },
  { value: BehaviorType.Reset, label: 'Reset', icon: 'reset' }
];

export const getFilterChangeAnnouncement = (filtered: number, total: number, childName?: string): string => {
  const childPart = childName ? ` for ${childName}` : '';
  return `Filtered to show ${filtered} of ${total} events${childPart}`;
};

export const getNoEventsMessage = (hasActiveFilters: boolean): string => {
  return hasActiveFilters ? 'No events match the current filters' : 'No events recorded yet';
};

const EventLog: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  // ThemeProvider's resolved theme and this module's naluV1Theme import different
  // instances under Vite HMR, so compare a stable identifying field rather than `===`.
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;

  const [selectedFamily, setSelectedFamily] = useState<FamilyWithChildren | null>(null);
  const [events, setEvents] = useState<BehaviorEventEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0); // Total count from server for pagination (Issue #1081)
  const [isLoading, setIsLoading] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'recordedAt', direction: 'desc' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedEvent, setSelectedEvent] = useState<BehaviorEventEntry | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editEventData, setEditEventData] = useState({
    behaviorType: BehaviorType.Star,
    notes: '',
    childId: 0,
    newEventTime: null as dayjs.Dayjs | null,
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>(
    { open: false, message: '', severity: 'success' }
  );
  const [error, setError] = useState<string | null>(null);
  const [dateValidationError, setDateValidationError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>(getDefaultFilterState());

  useEffect(() => {
    // Only load families data after user is authenticated
    if (user) {
      loadFamilies();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (selectedFamily) {
      loadChildrenAndEvents();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFamily]);

  useEffect(() => {
    if (selectedFamily) {
      loadEvents();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, rowsPerPage]); // Reload events when filters, page, or page size changes (Issue #1081)

  const loadFamilies = async () => {
    try {
      const familiesData = await familyAPI.getFamilies();
      if (familiesData.length > 0 && !selectedFamily) {
        // Get detailed family info with correct createdAt timestamp (fixes Issue #210)
        const familyDetails = await familyAPI.getFamilyDetails(familiesData[0].familyId);
        
        // Load children for the first family
        const childrenData = await familyAPI.getChildren(familiesData[0].familyId);
        const familyWithChildren: FamilyWithChildren = {
          ...familyDetails, // Use detailed family info instead of summary
          children: childrenData || []
        };
        setSelectedFamily(familyWithChildren);
      }
      setError(null);
    } catch (error: unknown) {
      console.error('EventLog: Failed to load families:', error);
      // 401 errors are now handled by the global interceptor
      const message = error instanceof Error ? error.message : String(error);
      setError(`Failed to load families: ${message}`);
      showSnackbar('Failed to load families', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadChildrenAndEvents = async () => {
    if (!selectedFamily) {
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      
      const childrenData = await familyAPI.getChildren(selectedFamily.familyId);
      
      // If no children, set empty events and finish loading
      if (!childrenData || childrenData.length === 0) {
        setEvents([]);
        setIsLoading(false);
        return;
      }
      
      // Load all family behavior records using server-side filtering
      await loadEvents();
      
    } catch (error) {
      console.error('EventLog: Failed to load children and events:', error);
      setError(`Failed to load event data: ${error instanceof Error ? error.message : 'Unknown error'}`);
      showSnackbar('Failed to load event data', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadEvents = async () => {
    if (!selectedFamily) return;
    
    try {
      
      // Convert frontend filter format to backend format
      const childIdParam = filters.selectedChildId;
      const behaviorTypesParam = filters.selectedBehaviorTypes.map(type => {
        switch(type) {
          case BehaviorType.Star: return 'Star';
          case BehaviorType.Triangle: return 'Triangle';
          case BehaviorType.Square: return 'Square';
          case BehaviorType.FlipYourCoin: return 'FlipYourCoin';
          case BehaviorType.MakeItRight: return 'MakeItRight';
          case BehaviorType.OneOnOneTime: return 'OneOnOneTime';
          case BehaviorType.GoalNuke: return 'GoalNuke';
          case BehaviorType.Reset: return 'Reset';
          default: return 'Star'; // fallback, should never happen
        }
      });
      
      const response = await behaviorAPI.getBehaviorRecords(
        selectedFamily.familyId,
        undefined, // startDate
        undefined, // endDate
        childIdParam,
        behaviorTypesParam,
        undefined, // days
        page + 1, // API uses 1-based pagination (Issue #1081)
        rowsPerPage
      );

      // Transform to BehaviorEventEntry format for shared table component
      const eventEntries: BehaviorEventEntry[] = response.data.map((behavior: BehaviorRecord) => ({
        behaviorId: behavior.behaviorId,
        childId: behavior.childId,
        childName: getChildNameById(behavior.childId),
        familyName: selectedFamily.name,
        behaviorType: behavior.behaviorType,
        notes: behavior.notes,
        recordedAt: behavior.recordedAt,
        eventTime: behavior.eventTime,
        eventTimezone: behavior.eventTimezone,
        eventLocalTime: behavior.eventLocalTime,
        recordedByUserName: behavior.recordedByUserName || 'Unknown User',
        recordedByUserId: behavior.recordedByUserId,
        sourceDisplayName: behavior.sourceDisplayName,
        source: behavior.source?.toString(),
      }));


      setEvents(eventEntries);
      setTotalCount(response.totalCount); // Store total count for pagination (Issue #1081)
      
    } catch (error: unknown) {
      console.error('EventLog: Failed to load events:', error);
      const message = error instanceof Error ? error.message : String(error);
      setError(`Failed to load events: ${message}`);
      showSnackbar('Failed to load events', 'error');
    }
  };
  
  // Helper function to get child name by ID
  const getChildNameById = (childId: number): string => {
    if (!selectedFamily?.children) return 'Unknown Child';
    const child = selectedFamily.children.find(c => (c.childId || c.id) === childId);
    return child?.name || 'Unknown Child';
  };

  const handleAppBarMenuClick = (action: string) => {
    if (action === 'Dashboard') {
      navigate('/');
    } else if (action === '/event-log') {
      // Already on event log
    } else {
      showSnackbar(`${action} page coming soon!`, 'success');
    }
  };

  const handleBackClick = () => {
    navigate('/');
  };

  const handleSort = (field: string) => {
    const newDirection = sortConfig.field === field && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ field: field as keyof BehaviorEventEntry, direction: newDirection });
  };

  const canEditDelete = (event: BehaviorEventEntry) => {
    // For now, assume users can edit/delete their own events
    // You might want to add more sophisticated permission checking
    return event.recordedByUserId === user?.userId;
  };

  const handleEditEvent = (event: BehaviorEventEntry) => {
    setSelectedEvent(event);

    // Get the display time and parse as literal time value
    // We want to show the family-local time (e.g., 5:15 PM) in the DateTimePicker
    // regardless of the browser's timezone
    const displayTime = getDisplayTime(event);
    let newTime;

    if (event.eventLocalTime && displayTime === event.eventLocalTime) {
      // eventLocalTime is stored as "YYYY-MM-DDTHH:mm:ss+00:00" representing family-local time
      // Strip timezone suffix and parse as literal time values
      const localTimeString = displayTime.replace('+00:00', '').replace('Z', '');

      // Parse the time components manually to avoid timezone interpretation
      // dayjs() parsing of ISO strings applies timezone offsets, so we manually construct
      // the date from components to treat them as literal values
      const match = localTimeString.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
      if (match) {
        const [, year, month, day, hour, minute, second] = match;
        newTime = dayjs()
          .year(parseInt(year))
          .month(parseInt(month) - 1)
          .date(parseInt(day))
          .hour(parseInt(hour))
          .minute(parseInt(minute))
          .second(parseInt(second));
      } else {
        // Fallback to simple parsing
        newTime = dayjs(localTimeString);
      }
    } else {
      // For recordedAt or other times, use as-is
      newTime = dayjs(displayTime);
    }

    setEditEventData({
      behaviorType: event.behaviorType,
      notes: event.notes || '',
      childId: event.childId,
      newEventTime: newTime,
    });
    setDateValidationError(null);
    setIsEditModalOpen(true);
  };

  const handleDeleteEvent = (event: BehaviorEventEntry) => {
    setSelectedEvent(event);
    setIsDeleteDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedEvent || !selectedFamily) return;
    
    // Validate new event time if provided
    if (editEventData.newEventTime) {
      const dateError = getDateValidationError(editEventData.newEventTime.toISOString(), selectedFamily.createdAt);
      if (dateError) {
        setDateValidationError(dateError);
        return;
      }
    }
    
    try {
      
      // Update the behavior record via API
      // Send literal time values (no timezone conversion) - backend will interpret as family-local
      await behaviorAPI.updateBehavior(
        selectedEvent.behaviorId,
        editEventData.behaviorType,
        editEventData.notes,
        editEventData.childId,
        editEventData.newEventTime?.format('YYYY-MM-DDTHH:mm:ss')
      );
      
      // Reload the children and events to get fresh data
      setEvents([]);
      
      // Small delay to ensure React processes the state change
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Now load fresh data
      await loadChildrenAndEvents();
      
      showSnackbar('Event updated successfully!', 'success');
      setIsEditModalOpen(false);
      setSelectedEvent(null);
    } catch (error: unknown) {
      console.error('Failed to update event:', error);
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        showSnackbar('Edit functionality not yet available - backend endpoint missing', 'error');
      } else {
        showSnackbar('Failed to update event', 'error');
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedEvent) return;
    
    
    try {
      // Delete the behavior record via API
      await behaviorAPI.deleteBehavior(selectedEvent.behaviorId);
      
      // Reload the children and events to get fresh data
      await loadChildrenAndEvents();
      
      showSnackbar('Event deleted successfully!', 'success');
      setIsDeleteDialogOpen(false);
      setSelectedEvent(null);
    } catch (error: unknown) {
      console.error('Failed to delete event:', error);

      // Provide specific error messaging based on status code
      let errorMessage = 'Failed to delete event';
      if (axios.isAxiosError(error)) {
        console.error('Error response data:', error.response?.data);
        console.error('Error response status:', error.response?.status);
        console.error('Error response headers:', error.response?.headers);

        const responseData = error.response?.data as { message?: string; title?: string; error?: string } | undefined;

        if (error.response?.status === 400) {
          const backendMessage = responseData?.message || responseData?.title || responseData?.error;
          if (backendMessage) {
            errorMessage = backendMessage; // Use the backend message directly since it's user-friendly
          } else {
            errorMessage = 'Cannot delete: This behavior may be too old or you may not have permission';
          }
        // 401 errors are now handled by the global interceptor
        } else if (error.response?.status === 403) {
          errorMessage = 'Permission denied - you can only delete your own behavior records';
        } else if (error.response?.status === 404) {
          errorMessage = 'Behavior record not found - it may have already been deleted';
        }
      }

      showSnackbar(errorMessage, 'error');
    }
  };

  const handleMicrophonePress = () => {
    setIsRecording(true);
  };

  const handleMicrophoneRelease = () => {
    setIsRecording(false);
    showSnackbar('Voice recording feature coming soon!', 'success');
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  // Add error boundary logging
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error('EventLog: Unhandled error:', event.error);
      setError(`Unhandled error: ${event.error?.message || 'Unknown error'}`);
      setIsLoading(false);
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);



  // Get the display time for an event (enhanced time fields for timeline stability - Issue #312)
  const getDisplayTime = (event: BehaviorEventEntry) => {
    // Use eventLocalTime first (what user saw), then fallback to eventTime, then recordedAt
    return event.eventLocalTime || event.eventTime || event.recordedAt;
  };

  // Server-side filtering means events are already filtered, just sort
  const sortedEvents = [...events].sort((a, b) => {
    // Special handling for date sorting - use eventTime for stable timeline ordering (Issue #312)
    let aValue, bValue;
    if (sortConfig.field === 'recordedAt') {
      // Use eventTime for stable sorting, fallback to display time
      aValue = a.eventTime || getDisplayTime(a);
      bValue = b.eventTime || getDisplayTime(b);
    } else {
      aValue = a[sortConfig.field];
      bValue = b[sortConfig.field];
    }
    
    // Handle undefined values
    if (aValue === undefined && bValue === undefined) return 0;
    if (aValue === undefined) return 1;
    if (bValue === undefined) return -1;
    
    if (sortConfig.direction === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography>Loading Event Log...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh" p={4}>
        <Typography variant="h5" color="error" gutterBottom>
          Something went wrong
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center" mb={2}>
          {error}
        </Typography>
        <Button variant="contained" onClick={() => {
          setError(null);
          setIsLoading(true);
          loadFamilies();
        }}>
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <SharedAppBar 
        title="Event Log" 
        showBackButton={true}
        onBackClick={handleBackClick}
        onMenuClick={handleAppBarMenuClick}
      />

      {/* Page Title Container */}
      <Box sx={{ bgcolor: isNaluV1Theme ? 'background.default' : 'grey.50', py: 2 }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {/* fontWeight 500 is below h4's baseline 600 but not the canonical 400 lightening value; left as an intentional partial de-emphasis */}
            <Typography variant="h4" sx={{ fontWeight: 500, color: 'text.primary' }}>
              Event Log
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' }, 
              gap: 2, 
              alignItems: { xs: 'stretch', md: 'center' },
              flexWrap: 'wrap'
            }}>
              {/* Child Filter Dropdown */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Filter by child</InputLabel>
                <Select
                  value={filters.selectedChildId || ''}
                  label="Filter by child"
                  onChange={(e) => {
                    const targetValue = String(e.target.value);
                    const value = targetValue === '' ? null : Number(targetValue);
                    setFilters({ ...filters, selectedChildId: value });
                    setPage(0); // Reset to page 0 when filter changes
                  }}
                  aria-label="Filter by child"
                >
                  {selectedFamily && getChildDropdownOptions(selectedFamily.children).map((option) => (
                    <MenuItem key={option.value || 'all'} value={option.value || ''}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Behavior Type Filter Checkboxes */}
              <FormGroup row sx={{ gap: 1, flexWrap: 'wrap' }}>
                {getBehaviorTypeOptions().map((option) => (
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        checked={filters.selectedBehaviorTypes.includes(option.value)}
                        onChange={(e) => {
                          const newTypes = e.target.checked
                            ? [...filters.selectedBehaviorTypes, option.value]
                            : filters.selectedBehaviorTypes.filter(type => type !== option.value);
                          setFilters({ ...filters, selectedBehaviorTypes: newTypes });
                          setPage(0); // Reset to page 0 when filter changes
                        }}
                        aria-label={`Filter by ${option.label.toLowerCase()} behavior`}
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {/* Icon glyph sizing via fontSize, not a Typography text override */}
                        {getBehaviorIcon(option.value, { sx: { fontSize: 16, color: getBehaviorColor(option.value) } })}
                        <Typography variant="body2">{option.label}</Typography>
                      </Box>
                    }
                  />
                ))}
              </FormGroup>

              {/* Event Counter */}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  ml: { xs: 0, md: 'auto' },
                  fontWeight: 600,
                  alignSelf: { xs: 'flex-start', md: 'center' },
                  order: { xs: -1, md: 0 }
                }}
                role="status"
                aria-label="Event count status"
                aria-live="polite"
              >
                {getEventCounterText(events.length, totalCount)}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Container */}
      <Container maxWidth="xl" sx={{ flexGrow: 1, pt: 3, pb: 1, px: { xs: 1, sm: 2 } }}>
        {events.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography variant="h5" gutterBottom>
              {getNoEventsMessage(events.length > 0)}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {events.length > 0 
                ? 'Try adjusting your filters to see more events.'
                : 'Behavior events will appear here as they are recorded.'
              }
            </Typography>
          </Box>
        ) : (
          <BehaviorEventsTable
            events={sortedEvents}
            totalCount={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            sortConfig={sortConfig}
            loading={isLoading}
            showChildColumn={true}
            showFamilyColumn={false}
            showEditActions={true}
            onPageChange={setPage}
            onRowsPerPageChange={(newRowsPerPage) => {
              setRowsPerPage(newRowsPerPage);
              setPage(0);
            }}
            onSort={handleSort}
            onEdit={handleEditEvent}
            onDelete={handleDeleteEvent}
            canEditDelete={canEditDelete}
          />
        )}
      </Container>


      {/* Edit Event Modal */}
      <Dialog
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Event</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {/* Child Selection Dropdown - Top Priority */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Child</InputLabel>
              <Select
                value={editEventData.childId}
                label="Child"
                onChange={(e) => setEditEventData({ ...editEventData, childId: e.target.value as number })}
              >
                {selectedFamily?.children.map((child) => (
                  <MenuItem key={child.id} value={child.id}>
                    {child.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Behavior Type Selection */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Behavior Type</InputLabel>
              <Select
                value={editEventData.behaviorType}
                label="Behavior Type"
                onChange={(e) => setEditEventData({ ...editEventData, behaviorType: e.target.value as BehaviorType })}
              >
                {Object.values(BehaviorType)
                  .filter((v): v is BehaviorType => typeof v === 'number')
                  .map((type) => (
                    <MenuItem key={type} value={type}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {/* Icon glyph sizing via fontSize, not a Typography text override */}
                        {getBehaviorIcon(type, { sx: { fontSize: 16, mr: 1 } })}
                        {getBehaviorLabel(type)}
                      </Box>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes"
              value={editEventData.notes}
              onChange={(e) => setEditEventData({ ...editEventData, notes: e.target.value })}
              placeholder="Add notes about this behavior..."
              sx={{ mb: 2 }}
            />

            {/* Read-only source display */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Recording Source
              </Typography>
              <Chip
                label={selectedEvent?.sourceDisplayName || 'Unknown'}
                size="small"
                variant="outlined"
                color="default"
                // Chip has no Typography variant to swap to; fontSize matches body2/button but this is a standalone Chip label
                sx={{ fontSize: '0.875rem' }}
              />
            </Box>
            
            {/* Date/Time Controls - Bottom Priority */}
            <Box sx={{ mb: 2 }}>
              <DateTimePicker
                label="New event time (optional)"
                value={editEventData.newEventTime}
                onChange={(newValue) => {
                  setEditEventData({ ...editEventData, newEventTime: newValue as Dayjs | null });
                  setDateValidationError(null);
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: 'Leave blank to keep current time',
                    error: Boolean(dateValidationError),
                    inputProps: {
                      'data-testid': 'event-time-input'
                    }
                  }
                }}
              />
              {dateValidationError && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {dateValidationError}
                </Typography>
              )}
            </Box>

            {/* Current Event Time Display */}
            {selectedEvent && (() => {
              const displayTime = getDisplayTime(selectedEvent);
              const familyTimezone = selectedFamily?.timezone;

              // Event was edited if EventTime exists and differs from recordedAt
              // Both are UTC timestamps, so we can compare them directly
              const wasEdited = selectedEvent.eventTime &&
                selectedEvent.eventTime !== selectedEvent.recordedAt &&
                dayjs(selectedEvent.eventTime).format('YYYY-MM-DD HH:mm') !== dayjs(selectedEvent.recordedAt).format('YYYY-MM-DD HH:mm');

              return (
                <Box sx={{ mb: 2, p: 2, bgcolor: isNaluV1Theme ? 'background.default' : 'grey.50', borderRadius: isNaluV1Theme ? '8px' : 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {wasEdited ? 'Current Event Time' : 'Event Time'}
                  </Typography>
                  <Typography variant="h6" color="text.primary" gutterBottom>
                    {formatEventLocalTime(displayTime)}
                  </Typography>
                  {wasEdited && (
                    <Typography variant="caption" color="text.secondary">
                      Originally logged: {formatUtcTimeInTimezone(selectedEvent.recordedAt, familyTimezone)} ({formatRelativeTime(selectedEvent.recordedAt, familyTimezone)})
                    </Typography>
                  )}
                </Box>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this behavior event? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Floating Microphone */}
      <Fab
        color="secondary"
        size="large"
        data-testid="mic-button"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
          transform: isRecording ? 'scale(1.1)' : 'scale(1)',
          transition: 'transform 0.2s',
          bgcolor: isRecording ? 'error.main' : 'secondary.main',
          display: 'none', // Hidden until voice recording feature is ready
        }}
        onMouseDown={handleMicrophonePress}
        onMouseUp={handleMicrophoneRelease}
        onTouchStart={handleMicrophonePress}
        onTouchEnd={handleMicrophoneRelease}
      >
        <MicIcon />
      </Fab>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default EventLog;